
import React, { useState, useEffect } from 'react';
import { Line, Product, System, ModalActionType, SystemInterfaceType } from '../types';
import { MOCK_PRODUCT_DATA, PlusCircleIcon } from '../constants';
import LineTable from './LineTable';
import LineFormModalV2 from './LineFormModalV2';
import DeleteLineModal from './DeleteLineModal';
import SystemTable from './SystemTable';
import SystemFormModalV2 from './SystemFormModalV2';
import DeleteSystemModal from './DeleteSystemModal';
import ShiftsSettings from './ShiftsSettings';
import DefaultsSettings from './DefaultsSettings';
import UsersSettings from './UsersSettings';
import CalibrationPage from './CalibrationPage';
import HelpIcon from './HelpIcon';
// Using database instead of localStorage
import { useInterface } from '../contexts/InterfaceContext';
import { useDatabaseContext } from '../contexts/DatabaseContext';
import { usePreferences } from '../contexts/PreferencesContext';

const generateInternalId = (prefix: string) => `${prefix}-internal-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

type ActiveSettingsTab = 'calibration' | 'users' | 'systems' | 'lines' | 'shifts' | 'defaults' | 'preferences';
// Removed localStorage keys - using database now

// Common timezones organized by region
const TIMEZONE_OPTIONS = [
  { label: 'UTC (Coordinated Universal Time)', value: 'UTC' },
  // Americas
  { label: 'US Eastern (New York)', value: 'America/New_York' },
  { label: 'US Central (Chicago)', value: 'America/Chicago' },
  { label: 'US Mountain (Denver)', value: 'America/Denver' },
  { label: 'US Pacific (Los Angeles)', value: 'America/Los_Angeles' },
  { label: 'Canada Eastern (Toronto)', value: 'America/Toronto' },
  { label: 'Mexico City', value: 'America/Mexico_City' },
  { label: 'Brazil (São Paulo)', value: 'America/Sao_Paulo' },
  // Europe
  { label: 'UK (London)', value: 'Europe/London' },
  { label: 'Central Europe (Berlin)', value: 'Europe/Berlin' },
  { label: 'France (Paris)', value: 'Europe/Paris' },
  { label: 'Spain (Madrid)', value: 'Europe/Madrid' },
  { label: 'Italy (Rome)', value: 'Europe/Rome' },
  { label: 'Russia (Moscow)', value: 'Europe/Moscow' },
  // Asia
  { label: 'India (Kolkata)', value: 'Asia/Kolkata' },
  { label: 'China (Shanghai)', value: 'Asia/Shanghai' },
  { label: 'Japan (Tokyo)', value: 'Asia/Tokyo' },
  { label: 'South Korea (Seoul)', value: 'Asia/Seoul' },
  { label: 'Singapore', value: 'Asia/Singapore' },
  { label: 'UAE (Dubai)', value: 'Asia/Dubai' },
  // Pacific
  { label: 'Australia Eastern (Sydney)', value: 'Australia/Sydney' },
  { label: 'Australia Central (Adelaide)', value: 'Australia/Adelaide' },
  { label: 'Australia Western (Perth)', value: 'Australia/Perth' },
  { label: 'New Zealand (Auckland)', value: 'Pacific/Auckland' },
];

const SettingsPage: React.FC = () => {
  const { interfaceType, setInterfaceType } = useInterface();
  const { apiUrl } = useDatabaseContext();
  const { weightUnit, setWeightUnit } = usePreferences();
  const [activeTab, setActiveTab] = useState<ActiveSettingsTab>('calibration');
  const [timezone, setTimezone] = useState<string>('UTC');
  const [calibrationInterval, setCalibrationInterval] = useState<number>(24);

  const [allProducts, setAllProducts] = useState<Product[]>([]);
  const [productsLoading, setProductsLoading] = useState(true);
  const [linesLoading, setLinesLoading] = useState(true);
  const [systemsLoading, setSystemsLoading] = useState(true);

  const [lines, setLines] = useState<Line[]>([]);
  const [systems, setSystems] = useState<System[]>([]);

  const [isLineModalOpen, setIsLineModalOpen] = useState(false);
  const [lineModalAction, setLineModalAction] = useState<ModalActionType | null>(null);
  const [selectedLine, setSelectedLine] = useState<Line | null>(null);
  const [lineSearchTerm, setLineSearchTerm] = useState('');

  const [isSystemModalOpen, setIsSystemModalOpen] = useState(false);
  const [systemModalAction, setSystemModalAction] = useState<ModalActionType | null>(null);
  const [selectedSystem, setSelectedSystem] = useState<System | null>(null);
  const [systemSearchTerm, setSystemSearchTerm] = useState('');

  // Helper function to sort lines by hardware ID (nulls last), then by name
  const sortLinesByHardwareId = (lines: Line[]): Line[] => {
    return [...lines].sort((a, b) => {
      // Handle null/undefined hardware IDs
      if (a.hardwareId === null || a.hardwareId === undefined) {
        if (b.hardwareId === null || b.hardwareId === undefined) {
          return a.name.localeCompare(b.name);
        }
        return 1; // a goes after b
      }
      if (b.hardwareId === null || b.hardwareId === undefined) {
        return -1; // a goes before b
      }
      // Both have hardware IDs, sort numerically
      const diff = Number(a.hardwareId) - Number(b.hardwareId);
      if (diff === 0) {
        return a.name.localeCompare(b.name);
      }
      return diff;
    });
  };


  // Fetch system preferences including timezone
  useEffect(() => {
    const fetchPreferences = async () => {
      try {
        const response = await fetch(`${apiUrl}/system-preferences`);
        if (response.ok) {
          const data = await response.json();
          if (data.timezone) {
            setTimezone(data.timezone);
          }
          if (data.interfaceType) {
            setInterfaceType(data.interfaceType);
          }
          if (data.calibration_interval_hours !== undefined) {
            setCalibrationInterval(data.calibration_interval_hours);
          }
        }
      } catch (error) {
        console.error('Error fetching system preferences:', error);
      }
    };
    fetchPreferences();
  }, [apiUrl, setInterfaceType]);

  // Fetch lines from the database
  const fetchLines = async () => {
    try {
      setLinesLoading(true);
      const response = await fetch(`${apiUrl}/lines`);
      if (response.ok) {
        const data = await response.json();
        // Sort lines by hardware ID (nulls last), then by name
        const sortedLines = sortLinesByHardwareId(data.data || []);
        setLines(sortedLines);
      }
    } catch (error) {
      console.error('Error fetching lines:', error);
      setLines([]);
    } finally {
      setLinesLoading(false);
    }
  };

  useEffect(() => {
    fetchLines();
  }, [apiUrl]);

  // Fetch systems from the database
  const fetchSystems = async () => {
    try {
      setSystemsLoading(true);
      const response = await fetch(`${apiUrl}/systems`);
      if (response.ok) {
        const data = await response.json();
        setSystems(data.data || []);
      }
    } catch (error) {
      console.error('Error fetching systems:', error);
    } finally {
      setSystemsLoading(false);
    }
  };

  useEffect(() => {
    fetchSystems();
  }, [apiUrl]);

  // Fetch products from the database
  useEffect(() => {
    const fetchProducts = async () => {
      try {
        setProductsLoading(true);
        const response = await fetch(`${apiUrl}/products`);
        if (response.ok) {
          const data = await response.json();
          setAllProducts(data.data || []);
        }
      } catch (error) {
        console.error('Error fetching products:', error);
      } finally {
        setProductsLoading(false);
      }
    };
    fetchProducts();
  }, [apiUrl]);


  const handleOpenLineModal = (action: ModalActionType, line?: Line) => {
    setLineModalAction(action);
    setSelectedLine(line || null);
    setIsLineModalOpen(true);
  };

  const handleCloseLineModal = () => {
    setIsLineModalOpen(false);
    setLineModalAction(null);
    setSelectedLine(null);
  };

  const handleOpenSystemModal = (action: ModalActionType, system?: System) => {
    setSystemModalAction(action);
    setSelectedSystem(system || null);
    setIsSystemModalOpen(true);
  };

  const handleCloseSystemModal = () => {
    setIsSystemModalOpen(false);
    setSystemModalAction(null);
    setSelectedSystem(null);
  };

  const refreshSystems = fetchSystems;

  const handleAddOrModifyLine = async (lineData: Omit<Line, 'id'> | Line, isNew: boolean) => {
    try {
      if (isNew) {
        const newLine = { 
          ...(lineData as Omit<Line, 'id'>), 
          id: generateInternalId('line') 
        };
        const response = await fetch(`${apiUrl}/lines`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(newLine)
        });
        if (response.ok) {
          const result = await response.json();
          // Add the new line and re-sort by hardware ID
          setLines(prev => sortLinesByHardwareId([result, ...prev]));
          handleCloseLineModal();
        } else {
          const error = await response.json();
          alert(error.error || 'Failed to create line');
          // Don't close modal on error so user can fix the issue
          return;
        }
      } else {
        const response = await fetch(`${apiUrl}/lines/${(lineData as Line).id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(lineData)
        });
        if (response.ok) {
          const updatedLine = await response.json();
          // Update the line with the response from the server and re-sort by hardware ID
          setLines(prev => sortLinesByHardwareId(
            prev.map(l => l.id === (lineData as Line).id ? updatedLine : l)
          ));
          handleCloseLineModal();
        } else {
          const error = await response.json();
          alert(error.error || 'Failed to update line');
          // Don't close modal on error so user can fix the issue
          return;
        }
      }
    } catch (error) {
      console.error('Error saving line:', error);
      alert('Network error: Failed to save line');
    }
  };

  const handleDeleteLine = async () => {
    if (selectedLine) {
      try {
        const response = await fetch(`${apiUrl}/lines/${selectedLine.id}`, {
          method: 'DELETE'
        });
        if (response.ok) {
          setLines(prev => prev.filter(l => l.id !== selectedLine.id));
        }
      } catch (error) {
        console.error('Error deleting line:', error);
      }
    }
    handleCloseLineModal();
  };

  const handleInterfaceTypeChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const newType = event.target.value as SystemInterfaceType;
    setInterfaceType(newType);
    
    // Save to database
    try {
      const response = await fetch(`${apiUrl}/system-preferences`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ interfaceType: newType })
      });
      if (!response.ok) {
        const error = await response.json();
        console.error('Failed to update interface type:', error);
      }
    } catch (error) {
      console.error('Error updating interface type:', error);
    }
  };

  const handleWeightUnitChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newUnit = event.target.value as 'LBS' | 'KGS';
    setWeightUnit(newUnit);
  };

  const handleTimezoneChange = async (event: React.ChangeEvent<HTMLSelectElement>) => {
    const newTimezone = event.target.value;
    setTimezone(newTimezone);
    
    // Save to database
    try {
      const response = await fetch(`${apiUrl}/system-preferences`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ timezone: newTimezone })
      });
      if (!response.ok) {
        const error = await response.json();
        console.error('Failed to update timezone:', error);
      }
    } catch (error) {
      console.error('Error updating timezone:', error);
    }
  };

  const handleCalibrationIntervalChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const newInterval = parseInt(event.target.value) || 24;
    setCalibrationInterval(newInterval);
    
    // Save to database
    try {
      const response = await fetch(`${apiUrl}/system-preferences`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ calibration_interval_hours: newInterval })
      });
      if (!response.ok) {
        const error = await response.json();
        console.error('Failed to update calibration interval:', error);
      }
    } catch (error) {
      console.error('Error updating calibration interval:', error);
    }
  };

  const filteredLines = lines.filter(line =>
    line.name.toLowerCase().includes(lineSearchTerm.toLowerCase())
  );

  const filteredSystems = systems.filter(system =>
    system.name.toLowerCase().includes(systemSearchTerm.toLowerCase())
  );

  const tabBaseStyle = "py-3 px-5 text-sm font-medium text-center cursor-pointer focus:outline-none";
  const activeTabStyle = "border-b-2 border-indigo-600 text-indigo-600";
  const inactiveTabStyle = "border-b-2 border-transparent text-slate-500 hover:text-indigo-600 hover:border-indigo-500";

  return (
    <div className="p-4 md:p-8 space-y-6 md:space-y-8">
      <div className="flex items-center gap-2">
        <h1 className="text-2xl md:text-3xl font-semibold text-slate-800">Settings</h1>
        <HelpIcon section="settings/system-config" tooltip="Learn about system settings" />
      </div>

      <div className="border-b border-slate-200">
        <nav className="-mb-px flex space-x-4" aria-label="Settings Tabs">
          <button
            onClick={() => setActiveTab('calibration')}
            className={`${tabBaseStyle} ${activeTab === 'calibration' ? activeTabStyle : inactiveTabStyle}`}
            aria-current={activeTab === 'calibration' ? 'page' : undefined}
          >
            Calibration
          </button>
          <button
            onClick={() => setActiveTab('systems')}
            className={`${tabBaseStyle} ${activeTab === 'systems' ? activeTabStyle : inactiveTabStyle}`}
            aria-current={activeTab === 'systems' ? 'page' : undefined}
          >
            Systems
          </button>
          <button
            onClick={() => setActiveTab('lines')}
            className={`${tabBaseStyle} ${activeTab === 'lines' ? activeTabStyle : inactiveTabStyle}`}
            aria-current={activeTab === 'lines' ? 'page' : undefined}
          >
            Lines
          </button>
          <button
            onClick={() => setActiveTab('shifts')}
            className={`${tabBaseStyle} ${activeTab === 'shifts' ? activeTabStyle : inactiveTabStyle}`}
            aria-current={activeTab === 'shifts' ? 'page' : undefined}
          >
            Shifts
          </button>
          <button
            onClick={() => setActiveTab('users')}
            className={`${tabBaseStyle} ${activeTab === 'users' ? activeTabStyle : inactiveTabStyle}`}
            aria-current={activeTab === 'users' ? 'page' : undefined}
          >
            Users
          </button>
          <button
            onClick={() => setActiveTab('defaults')}
            className={`${tabBaseStyle} ${activeTab === 'defaults' ? activeTabStyle : inactiveTabStyle}`}
            aria-current={activeTab === 'defaults' ? 'page' : undefined}
          >
            System Defaults
          </button>
          <button
            onClick={() => setActiveTab('preferences')}
            className={`${tabBaseStyle} ${activeTab === 'preferences' ? activeTabStyle : inactiveTabStyle}`}
            aria-current={activeTab === 'preferences' ? 'page' : undefined}
          >
            System Preferences
          </button>
        </nav>
      </div>

      {/* Calibration Tab Content */}
      {activeTab === 'calibration' && (
        <CalibrationPage />
      )}

      {/* Users Tab Content */}
      {activeTab === 'users' && (
        <UsersSettings />
      )}

      {/* Systems Tab Content */}
      {activeTab === 'systems' && (
        <div className="space-y-6">
              <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
                <div className="flex items-center gap-2">
                  <h2 className="text-xl md:text-2xl font-semibold text-slate-700">Manage Systems</h2>
                  <HelpIcon section="settings/system-config" tooltip="Learn about systems" />
                </div>
                <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
                  <input
                    type="text"
                    placeholder="Search systems by name..."
                    className="px-4 py-2 border border-slate-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm w-full sm:w-auto"
                    value={systemSearchTerm}
                    onChange={(e) => setSystemSearchTerm(e.target.value)}
                    aria-label="Search systems"
                  />
                  <button
                    onClick={() => handleOpenSystemModal(ModalActionType.ADD)}
                    className="flex items-center justify-center px-4 py-2 text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 border border-transparent rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-colors"
                    aria-label="Add new system"
                  >
                    <PlusCircleIcon className="w-5 h-5 mr-2" />
                    Add System
                  </button>
                </div>
              </div>
              <SystemTable
                systems={filteredSystems}
                onModify={(system) => handleOpenSystemModal(ModalActionType.MODIFY, system)}
                onDelete={(system) => handleOpenSystemModal(ModalActionType.DELETE, system)}
              />
        </div>
      )}

      {/* Lines Tab Content */}
      {activeTab === 'lines' && (
        <div className="space-y-6">
              <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
                <div className="flex items-center gap-2">
                  <h2 className="text-xl md:text-2xl font-semibold text-slate-700">Manage Lines</h2>
                  <HelpIcon section="settings/system-config" tooltip="Learn about production lines" />
                </div>
                <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
                  <input
                    type="text"
                    placeholder="Search lines by name..."
                    className="px-4 py-2 border border-slate-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm w-full sm:w-auto"
                    value={lineSearchTerm}
                    onChange={(e) => setLineSearchTerm(e.target.value)}
                    aria-label="Search lines"
                  />
                  <button
                    onClick={() => handleOpenLineModal(ModalActionType.ADD)}
                    className="flex items-center justify-center px-4 py-2 text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 border border-transparent rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-colors"
                    aria-label="Add new line"
                  >
                    <PlusCircleIcon className="w-5 h-5 mr-2" />
                    Add Line
                  </button>
                </div>
              </div>
              <LineTable
                lines={filteredLines}
                onModify={(line) => handleOpenLineModal(ModalActionType.MODIFY, line)}
                onDelete={(line) => handleOpenLineModal(ModalActionType.DELETE, line)}
              />
        </div>
      )}

      {/* Shifts Tab Content */}
      {activeTab === 'shifts' && (
        <ShiftsSettings />
      )}

      {/* System Defaults Tab Content */}
      {activeTab === 'defaults' && (
        <div className="space-y-6">
          <DefaultsSettings />
        </div>
      )}


      {/* System Preferences Tab Content */}
      {activeTab === 'preferences' && (
        <div className="space-y-8">
              <div>
                <h2 className="text-xl md:text-2xl font-semibold text-slate-700 mb-4">System Preferences</h2>
                <p className="text-slate-600">Configure general system settings and display preferences.</p>
              </div>

              {/* Interface Type Section */}
              <div className="space-y-4">
                <h3 className="text-lg font-medium text-slate-700">Interface Type</h3>
                <fieldset className="space-y-3">
                  <legend className="sr-only">Select system interface type</legend>
                  <div>
                    <label className="flex items-center space-x-3 cursor-pointer p-3 rounded-md hover:bg-slate-50 transition-colors border border-slate-200">
                      <input
                        type="radio"
                        name="systemInterfaceType"
                        value={SystemInterfaceType.WEB}
                        checked={interfaceType === SystemInterfaceType.WEB}
                        onChange={handleInterfaceTypeChange}
                        className="form-radio h-4 w-4 text-indigo-600 border-slate-300 focus:ring-indigo-500 transition duration-150 ease-in-out"
                      />
                      <div>
                        <span className="text-slate-700 font-medium">Web Interface</span>
                        <p className="text-sm text-slate-500 mt-1">Optimized for desktop and laptop computers with mouse/keyboard input</p>
                      </div>
                    </label>
                  </div>
                  <div>
                    <label className="flex items-center space-x-3 cursor-pointer p-3 rounded-md hover:bg-slate-50 transition-colors border border-slate-200">
                      <input
                        type="radio"
                        name="systemInterfaceType"
                        value={SystemInterfaceType.TOUCH}
                        checked={interfaceType === SystemInterfaceType.TOUCH}
                        onChange={handleInterfaceTypeChange}
                        className="form-radio h-4 w-4 text-indigo-600 border-slate-300 focus:ring-indigo-500 transition duration-150 ease-in-out"
                      />
                      <div>
                        <span className="text-slate-700 font-medium">Touch Interface</span>
                        <p className="text-sm text-slate-500 mt-1">Optimized for tablets and touch-screen devices with larger touch targets</p>
                      </div>
                    </label>
                  </div>
                </fieldset>
              </div>

              {/* Weight Unit Section */}
              <div className="space-y-4 pt-6 border-t border-slate-200">
                <h3 className="text-lg font-medium text-slate-700">Weight Unit</h3>
                <p className="text-sm text-slate-600">Select the unit of measurement for weight display throughout the system.</p>
                <fieldset className="space-y-3">
                  <legend className="sr-only">Select weight unit</legend>
                  <div>
                    <label className="flex items-center space-x-3 cursor-pointer p-3 rounded-md hover:bg-slate-50 transition-colors border border-slate-200">
                      <input
                        type="radio"
                        name="weightUnit"
                        value="LBS"
                        checked={weightUnit === 'LBS'}
                        onChange={handleWeightUnitChange}
                        className="form-radio h-4 w-4 text-indigo-600 border-slate-300 focus:ring-indigo-500 transition duration-150 ease-in-out"
                      />
                      <div>
                        <span className="text-slate-700 font-medium">Pounds (LBS)</span>
                        <p className="text-sm text-slate-500 mt-1">Display all weights in pounds</p>
                      </div>
                    </label>
                  </div>
                  <div>
                    <label className="flex items-center space-x-3 cursor-pointer p-3 rounded-md hover:bg-slate-50 transition-colors border border-slate-200">
                      <input
                        type="radio"
                        name="weightUnit"
                        value="KGS"
                        checked={weightUnit === 'KGS'}
                        onChange={handleWeightUnitChange}
                        className="form-radio h-4 w-4 text-indigo-600 border-slate-300 focus:ring-indigo-500 transition duration-150 ease-in-out"
                      />
                      <div>
                        <span className="text-slate-700 font-medium">Kilograms (KGS)</span>
                        <p className="text-sm text-slate-500 mt-1">Display all weights in kilograms</p>
                      </div>
                    </label>
                  </div>
                </fieldset>
              </div>

              {/* Timezone Section */}
              <div className="space-y-4 pt-6 border-t border-slate-200">
                <h3 className="text-lg font-medium text-slate-700">System Timezone</h3>
                <p className="text-sm text-slate-600">Set the timezone for displaying dates and times throughout the system.</p>
                <div>
                  <label htmlFor="timezone" className="block text-sm font-medium text-slate-700 mb-2">
                    Display Timezone
                  </label>
                  <select
                    id="timezone"
                    name="timezone"
                    value={timezone}
                    onChange={handleTimezoneChange}
                    className="w-full md:w-96 px-3 py-2 border border-slate-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500"
                  >
                    {TIMEZONE_OPTIONS.map(option => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                  <p className="mt-2 text-sm text-slate-500">
                    This timezone will be used for displaying times on the Dashboard and in reports.
                  </p>
                </div>
              </div>

              {/* Calibration Interval Section */}
              <div className="space-y-4 pt-6 border-t border-slate-200">
                <h3 className="text-lg font-medium text-slate-700">Scale Calibration Interval</h3>
                <p className="text-sm text-slate-600">Set how many hours can pass before scales require recalibration.</p>
                <div>
                  <label htmlFor="calibrationInterval" className="block text-sm font-medium text-slate-700 mb-2">
                    Calibration Required After (hours)
                  </label>
                  <div className="flex items-center space-x-3">
                    <input
                      type="number"
                      id="calibrationInterval"
                      name="calibrationInterval"
                      value={calibrationInterval}
                      onChange={handleCalibrationIntervalChange}
                      min="1"
                      max="168"
                      className="w-32 px-3 py-2 border border-slate-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500"
                    />
                    <span className="text-sm text-slate-600">
                      hours ({calibrationInterval === 24 ? '1 day' : calibrationInterval === 168 ? '1 week' : `${calibrationInterval} hours`})
                    </span>
                  </div>
                  <div className="mt-3 space-y-1">
                    <p className="text-sm text-slate-500">Quick settings:</p>
                    <div className="flex space-x-2">
                      <button
                        type="button"
                        onClick={() => handleCalibrationIntervalChange({ target: { value: '8' } } as any)}
                        className="px-3 py-1 text-xs font-medium text-slate-700 bg-slate-100 hover:bg-slate-200 rounded-md transition-colors"
                      >
                        Every Shift (8h)
                      </button>
                      <button
                        type="button"
                        onClick={() => handleCalibrationIntervalChange({ target: { value: '24' } } as any)}
                        className="px-3 py-1 text-xs font-medium text-slate-700 bg-slate-100 hover:bg-slate-200 rounded-md transition-colors"
                      >
                        Daily (24h)
                      </button>
                      <button
                        type="button"
                        onClick={() => handleCalibrationIntervalChange({ target: { value: '48' } } as any)}
                        className="px-3 py-1 text-xs font-medium text-slate-700 bg-slate-100 hover:bg-slate-200 rounded-md transition-colors"
                      >
                        Every 2 Days
                      </button>
                      <button
                        type="button"
                        onClick={() => handleCalibrationIntervalChange({ target: { value: '168' } } as any)}
                        className="px-3 py-1 text-xs font-medium text-slate-700 bg-slate-100 hover:bg-slate-200 rounded-md transition-colors"
                      >
                        Weekly
                      </button>
                    </div>
                  </div>
                  <p className="mt-2 text-sm text-slate-500">
                    Scales will show as "Needs Calibration" after this time period has elapsed since the last calibration.
                  </p>
                </div>
              </div>
        </div>
      )}

      {/* System Modals */}
      {(systemModalAction === ModalActionType.ADD || systemModalAction === ModalActionType.MODIFY) && (
        <SystemFormModalV2
          isOpen={isSystemModalOpen}
          onClose={handleCloseSystemModal}
          actionType={systemModalAction}
          system={selectedSystem}
          onRefresh={refreshSystems}
        />
      )}
      {systemModalAction === ModalActionType.DELETE && selectedSystem && (
        <DeleteSystemModal
          isOpen={isSystemModalOpen}
          onClose={handleCloseSystemModal}
          system={selectedSystem}
          onRefresh={refreshSystems}
        />
      )}

      {/* Line Modals */}
      {(lineModalAction === ModalActionType.ADD || (lineModalAction === ModalActionType.MODIFY && selectedLine)) && (
        <LineFormModalV2
          isOpen={isLineModalOpen}
          onClose={handleCloseLineModal}
          onSubmit={handleAddOrModifyLine}
          initialData={lineModalAction === ModalActionType.MODIFY ? selectedLine : null}
          title={lineModalAction === ModalActionType.ADD ? "Add New Line" : "Modify Line"}
        />
      )}
      {lineModalAction === ModalActionType.DELETE && selectedLine && (
        <DeleteLineModal
          isOpen={isLineModalOpen}
          onClose={handleCloseLineModal}
          onConfirm={handleDeleteLine}
          line={selectedLine}
        />
      )}
    </div>
  );
};

export default SettingsPage;